/**
 * Register/edit a partner company — docs/modules/partnerLeads.model.md §3.
 * `account_id`/`primary_contact_id` (crm.partner's optional links to an
 * existing account/contact record) are not exposed here — wiring a full
 * account/contact search picker is a fair follow-up, not something this
 * screen needed to block on.
 */
import { useEffect, useState } from 'react';
import { Modal, Field } from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';

const PARTNER_TYPES = ['Reseller', 'Referral', 'Technology', 'Implementation', 'Distributor'];

const BLANK = { name: '', partner_type: '', tier: '', commission_pct: '', is_active: true };

export function PartnerFormModal({ open, onClose, onSubmit, busy, partner }) {
  const [form, setForm] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const editing = Boolean(partner);

  useEffect(() => {
    if (!open) return;
    if (partner) {
      setForm({
        name: partner.name ?? '',
        partner_type: partner.partner_type ?? '',
        tier: partner.tier ?? '',
        commission_pct: partner.commission_pct ?? '',
        is_active: partner.is_active,
      });
    } else {
      setForm(BLANK);
    }
    setErrors({});
  }, [open, partner]);

  const set = (k) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: value }));
    setErrors((er) => (er[k] ? { ...er, [k]: undefined } : er));
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setErrors({ name: 'A name is required.' }); return; }

    const payload = { name: form.name.trim(), is_active: form.is_active };
    if (form.partner_type) payload.partner_type = form.partner_type;
    if (form.tier.trim()) payload.tier = form.tier.trim();
    if (form.commission_pct !== '') payload.commission_pct = Number(form.commission_pct);
    onSubmit(payload);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit partner' : 'Register a partner'}
      footer={
        <>
          <button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
          <button type="submit" form="partnerForm" className="btn btn-save btn-sm-x" disabled={busy}>
            {busy ? <><ButtonSpinner />Saving…</> : editing ? 'Save changes' : 'Register partner'}
          </button>
        </>
      }
    >
      <form id="partnerForm" onSubmit={submit} noValidate>
        <div className="row g-3">
          <div className="col-md-8">
            <Field label="Company name" required error={errors.name}>
              <input className="form-control" value={form.name} onChange={set('name')} autoFocus />
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Type">
              <select className="form-select" value={form.partner_type} onChange={set('partner_type')}>
                <option value="">Not set</option>
                {PARTNER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
          </div>
          <div className="col-md-4">
            <Field label="Tier"><input className="form-control" value={form.tier} onChange={set('tier')} placeholder="e.g. Gold" /></Field>
          </div>
          <div className="col-md-4">
            <Field label="Commission %">
              <input className="form-control" type="number" min="0" max="100" step="0.5"
                     value={form.commission_pct} onChange={set('commission_pct')} />
            </Field>
          </div>
          {editing && (
            <div className="col-md-4 d-flex align-items-end">
              <div className="form-check">
                <input className="form-check-input" type="checkbox" id="partnerActive"
                       checked={form.is_active} onChange={set('is_active')} />
                <label className="form-check-label t-sm" htmlFor="partnerActive">Active</label>
              </div>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}
